import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import api, { API } from "./api";
import { useAuth } from "./auth";
import { playMessageSound, playStatusSound } from "./notify";
import { notifyNative, notifyOS, ensureWebNotificationPermission, isNative, vibrate, initPush } from "./native";
import { toast } from "sonner";

const NotifCtx = createContext({
  chatUnread: 0,
  bookingsBadge: 0,
  byBooking: {},
  pendingActions: 0,
  disputesPending: 0,
  pulse: 0,
  triggerPulse: () => {},
});

const POLL_INTERVAL_MS = 8000;

// Only "new action required" statuses count toward the bottom-nav badge.
const OWNER_ACTION_STATUSES = new Set([
  "AGUARDANDO_ACEITE",  // alguém pediu para alugar — dono precisa responder
]);
const RENTER_ACTION_STATUSES = new Set([]);

// Helper para abrir o chat de uma reserva específica via deep-link
function deepLinkToChat(bookingId) {
  try {
    const url = `/minhas-reservas?openChat=${bookingId}`;
    if (window.location.pathname === "/minhas-reservas") {
      // Já está na tela — só atualiza query string + dispara evento custom
      window.history.replaceState({}, "", url);
      window.dispatchEvent(new CustomEvent("aj:openChat", { detail: { bookingId } }));
    } else {
      window.location.assign(url);
    }
  } catch (e) {
    console.error("[notify] deep link failed", e);
  }
}

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [chatUnread, setChatUnread] = useState(0);
  const [byBooking, setByBooking] = useState({});
  const [pendingActions, setPendingActions] = useState(0);
  const [disputesPending, setDisputesPending] = useState(0);
  const [pulse, setPulse] = useState(0); // incrementa a cada tick — consumido por MyBookings p/ refresh
  const prevChatRef = useRef(0);
  const prevActionsRef = useRef(0);
  const lastMessageIdRef = useRef(null);
  // Evita disparar toast de mensagens/solicitações JÁ EXISTENTES ao abrir/atualizar o app.
  // No 1º tick apenas "semeamos" os contadores (silencioso); toasts só em mudanças seguintes.
  const isFirstTickRef = useRef(true);

  const triggerPulse = useCallback(() => setPulse((p) => p + 1), []);

  // V14.3: badge na aba do navegador (web/PC) + permissão de notificação web
  const baseTitleRef = useRef(typeof document !== "undefined" ? (document.title || "AlugaJá") : "AlugaJá");
  const missedRef = useRef(0);
  const bumpTabBadge = useCallback(() => {
    if (typeof document === "undefined" || document.visibilityState === "visible") return;
    missedRef.current += 1;
    document.title = `(${missedRef.current}) ${baseTitleRef.current}`;
  }, []);
  const clearTabBadge = useCallback(() => {
    missedRef.current = 0;
    if (typeof document !== "undefined") document.title = baseTitleRef.current;
  }, []);

  // Pede permissão de notificação no navegador no 1º gesto do usuário (após login).
  useEffect(() => {
    if (!user || isNative()) return;
    const handler = () => { ensureWebNotificationPermission(); window.removeEventListener("pointerdown", handler); };
    window.addEventListener("pointerdown", handler, { once: true });
    return () => window.removeEventListener("pointerdown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Push remoto (FCM): registra o dispositivo. Logado → vinculado ao usuário;
  // no app sem login → registro ANÔNIMO (para campanhas de "finalize seu cadastro").
  useEffect(() => {
    if (user) {
      initPush((path, body) => api.post(path, body));
    } else if (isNative()) {
      initPush((path, body) => axios.post(`${API}${path}`, body), { anonymous: true });
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setChatUnread(0); setByBooking({}); setPendingActions(0); setDisputesPending(0);
      prevChatRef.current = 0; prevActionsRef.current = 0; lastMessageIdRef.current = null;
      isFirstTickRef.current = true;
      return;
    }
    let alive = true;

    const tick = async () => {
      try {
        const [a, b, rentals, bookings, preview, disputes] = await Promise.all([
          api.get("/chat/unread-count").catch(() => ({ data: { unread: 0 } })),
          api.get("/chat/unread-by-booking").catch(() => ({ data: {} })),
          api.get("/my/rentals").catch(() => ({ data: [] })),
          api.get("/my/bookings").catch(() => ({ data: [] })),
          api.get("/chat/unread-preview").catch(() => ({ data: [] })),
          api.get("/disputes/pending-count").catch(() => ({ data: { pending: 0 } })),
        ]);
        if (!alive) return;
        setDisputesPending(disputes.data?.pending || 0);
        const unread = a.data.unread || 0;
        const ownerActions = (rentals.data || []).filter((r) => OWNER_ACTION_STATUSES.has(r.status_locacao)).length;
        const renterActions = (bookings.data || []).filter((r) => RENTER_ACTION_STATUSES.has(r.status_locacao)).length;
        const actions = ownerActions + renterActions;
        const previewList = preview.data || [];

        // 1º tick (abertura/atualização do app): semeia contadores SEM avisar — não reavisa
        // mensagens/solicitações que o usuário já conhecia.
        if (!isFirstTickRef.current) {
          const visible = document.visibilityState === "visible";
          // Mensagem NOVA (id diferente da última vista)
          if (unread > prevChatRef.current && previewList.length > 0) {
            const latest = previewList[0];
            if (latest.message_id && latest.message_id !== lastMessageIdRef.current) {
              vibrate("medium");
              // App em 1º plano → toast + som no app. App em 2º plano → notificação nativa do SO.
              if (visible) {
                playMessageSound();
                toast.custom((t) => (
                  <div
                    onClick={() => { deepLinkToChat(latest.booking_id); toast.dismiss(t); }}
                    className="cursor-pointer bg-white rounded-xl shadow-lg border border-slate-200 p-3 w-full active:scale-[0.99] transition-transform"
                    data-testid="notif-toast-message"
                  >
                    <div className="font-bold text-sm text-slate-900">💬 {latest.sender_name}</div>
                    <div className="text-xs text-slate-600 line-clamp-2 mt-0.5">{latest.text || "Nova mensagem"}</div>
                    <div className="text-[11px] text-[#004E98] font-bold mt-1">Toque para abrir o chat →</div>
                  </div>
                ), { duration: 7000 });
              } else {
                if (!isNative()) { playMessageSound(); bumpTabBadge(); }
                notifyOS({
                  title: `💬 ${latest.sender_name || "Nova mensagem"}`,
                  body: latest.text || "Você recebeu uma nova mensagem",
                  url: `/minhas-reservas?openChat=${latest.booking_id}`,
                });
              }
            }
          }
          if (actions > prevActionsRef.current) {
            vibrate("heavy");
            if (visible) {
              playStatusSound();
              toast.custom((t) => (
                <div
                  onClick={() => {
                    toast.dismiss(t);
                    window.location.assign("/minhas-reservas?tab=rentals");
                  }}
                  className="cursor-pointer bg-white rounded-xl shadow-lg border border-slate-200 p-3 w-full active:scale-[0.99] transition-transform"
                  data-testid="notif-toast-request"
                >
                  <div className="font-bold text-sm text-slate-900">🛠️ Nova solicitação de aluguel</div>
                  <div className="text-xs text-slate-600 mt-0.5">Alguém pediu para alugar seu item.</div>
                  <div className="text-[11px] text-[#004E98] font-bold mt-1">Toque para responder →</div>
                </div>
              ), { duration: 6000 });
            } else {
              if (!isNative()) { playStatusSound(); bumpTabBadge(); }
              notifyOS({
                title: "🛠️ Nova solicitação de aluguel",
                body: "Você recebeu um novo pedido. Toque para responder.",
                url: "/minhas-reservas?tab=rentals",
              });
            }
          }
        }
        // Atualiza refs/estado (no 1º tick isto "semeia" silenciosamente).
        prevChatRef.current = unread;
        prevActionsRef.current = actions;
        if (previewList.length > 0 && previewList[0].message_id) {
          lastMessageIdRef.current = previewList[0].message_id;
        }
        isFirstTickRef.current = false;
        setChatUnread(unread);
        setByBooking(b.data || {});
        setPendingActions(actions);
        // Pulse leve a cada tick — MyBookings re-busca p/ refletir mudanças de status.
        setPulse((p) => p + 1);
      } catch (e) {
        console.error("[notify] tick failed", e?.message);
      }
    };

    tick();
    const t = setInterval(tick, POLL_INTERVAL_MS);

    // Re-tick ao voltar de outra aba
    const onFocus = () => { if (document.visibilityState === "visible") { clearTabBadge(); tick(); } };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);

    return () => {
      alive = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const bookingsBadge = chatUnread + pendingActions;

  const value = useMemo(
    () => ({ chatUnread, byBooking, bookingsBadge, pendingActions, disputesPending, pulse, triggerPulse, setChatUnread, setByBooking }),
    [chatUnread, byBooking, bookingsBadge, pendingActions, disputesPending, pulse, triggerPulse],
  );

  return (
    <NotifCtx.Provider value={value}>
      {children}
    </NotifCtx.Provider>
  );
}

export const useNotifications = () => useContext(NotifCtx);
